/**
 * 인증 상태 관리 슬라이스
 *
 * 사용자 인증 관련 전역 상태를 관리한다.
 * - 사용자 정보 조회, 로그인, 로그아웃 비동기 처리
 * - 언어 설정, 로그인 모달 표시 상태 관리
 *
 * 참고: 인증 라우트는 /auth 경로를 사용하므로
 * v2Client(baseURL: /api/v2)가 아닌 axios를 직접 사용한다.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import CryptoJS from 'crypto-js';

// ─── 비밀번호 해싱 (레거시 정책: PBKDF2-SHA512 / iter=789789 / salt=userId / keyLen=64 / Base64) ───
// 레거시는 Node.js crypto.pbkdf2(네이티브 C++)를 사용했지만
// 브라우저에서는 crypto-js(순수 JS)가 789,789회 반복에 20초 이상 걸림.
// → 브라우저 내장 Web Crypto API(crypto.subtle)를 사용해 네이티브 속도로 처리.
//   두 구현 모두 RFC 2898을 따르므로 동일한 입력에 동일한 출력을 생성.
const PBKDF2_ITERATIONS = 789789;
const PBKDF2_KEYLEN_BYTES = 64;

/**
 * Uint8Array → Base64 문자열 (Node.js Buffer.toString('base64')과 동일)
 */
function uint8ToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Web Crypto API 기반 고속 PBKDF2 (수백 ms 이내)
 * - 네이티브 구현이므로 메인 스레드 블로킹 최소화
 * - 레거시 Node.js crypto.pbkdf2와 동일한 결과 산출
 */
async function hashPasswordNative(password, userId) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: enc.encode(userId),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-512',
    },
    keyMaterial,
    PBKDF2_KEYLEN_BYTES * 8 // bit 단위
  );
  return uint8ToBase64(new Uint8Array(derivedBits));
}

/**
 * crypto-js 기반 폴백 — HTTP 환경(localhost 외 http://)에서 Web Crypto SubtleCrypto가
 * 제한될 때 사용. 동일한 결과를 생성하지만 매우 느림.
 */
function hashPasswordCryptoJs(password, userId) {
  const key = CryptoJS.PBKDF2(password, userId, {
    keySize: PBKDF2_KEYLEN_BYTES / 4,
    iterations: PBKDF2_ITERATIONS,
    hasher: CryptoJS.algo.SHA512,
  });
  return CryptoJS.enc.Base64.stringify(key);
}

/**
 * 비밀번호 해싱 — Web Crypto 우선, 실패 시 crypto-js 폴백
 */
async function hashPassword(password, userId) {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle && crypto.subtle.importKey) {
      return await hashPasswordNative(password, userId);
    }
  } catch (err) {
    console.warn('[authSlice] Web Crypto PBKDF2 실패 — crypto-js 폴백', err);
  }
  return hashPasswordCryptoJs(password, userId);
}

// 인증 관련 요청용 axios 인스턴스 (withCredentials 명시)
const authHttp = axios.create({
  withCredentials: true,
  timeout: 30000,
});

// ─── 비동기 Thunk 액션 ─────────────────────────────────────

/**
 * 현재 로그인된 사용자 정보 조회
 * 세션 기반 인증 확인에 사용
 */
export const fetchUserInfo = createAsyncThunk(
  'auth/fetchUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authHttp.get('/auth/get_userinfo');
      // 백엔드 응답 구조: { success: true, data: {...} }
      return response.data?.data ?? response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '사용자 정보 조회 실패');
    }
  }
);

/**
 * 사용자 로그인 처리
 *
 * 레거시 정책에 맞게 PBKDF2-SHA512로 해싱 후 Base64로 전송.
 * @param {Object} credentials - { userId, password } (평문 비밀번호)
 */
export const login = createAsyncThunk(
  'auth/login',
  async ({ userId, password }, { rejectWithValue }) => {
    try {
      // userId 공백 정규화 — salt/lookup 일관성 (일부 레거시 데이터의 USERID에 공백 포함)
      const normalizedUserId = String(userId || '').trim();
      // 레거시와 동일한 해싱 (클라이언트 사이드, Web Crypto 사용)
      const hashed = await hashPassword(password, normalizedUserId);
      const response = await authHttp.post('/auth/login', { userId: normalizedUserId, password: hashed });
      return response.data?.data ?? response.data;
    } catch (err) {
      const payload = err.response?.data;
      const code = payload?.errorCode || 'INVALID_CREDENTIALS';
      const message = payload?.error || '로그인 실패';
      return rejectWithValue({ code, message, meta: payload?.meta });
    }
  }
);

/**
 * 사용자 로그아웃 처리
 *
 * 서버 세션 종료 후 guest 사용자 정보를 재조회하여 Redux 상태에 반영한다.
 * 이를 통해 로그아웃 직후에도 템플릿 ID=0 기반의 guest 권한이 즉시 적용된다.
 */
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authHttp.get('/auth/logout');
      // 로그아웃 후 guest DTO 재조회 — user를 null로 두지 않고 guest 권한으로 채운다.
      try {
        const response = await authHttp.get('/auth/get_userinfo');
        return response.data?.data ?? response.data;
      } catch (err) {
        // get_userinfo 실패 시에도 로그아웃 자체는 성공 — null 반환
        return null;
      }
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '로그아웃 실패');
    }
  }
);

// ─── 초기 상태 ─────────────────────────────────────────────

const initialState = {
  user: null,            // 로그인된 사용자 정보 객체
  isLoggedIn: false,     // 로그인 여부
  initialized: false,    // 초기 get_userinfo 완료 여부(비회원 기본값 권한 포함)
  language: localStorage.getItem('buj-lang') || 'ko', // 현재 언어 설정 (ko/en)
  theme: localStorage.getItem('buj-theme') || 'light', // 테마 (light/dark)
  loginModalVisible: false, // 로그인 모달 표시 여부
  loading: false,        // 비동기 작업 로딩 상태
  error: null,           // 에러 메시지
  // ── 개발용 토글: 'guest' (비등록 사용자) | 'admin' (관리자)
  devRole: localStorage.getItem('buj-dev-role') || 'guest',
};

// ─── 슬라이스 정의 ─────────────────────────────────────────

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** 언어 설정 변경 (localStorage 영속화) */
    setLanguage(state, action) {
      state.language = action.payload;
      try { localStorage.setItem('buj-lang', action.payload); } catch (e) { /* noop */ }
    },
    /** 테마 설정 변경 (localStorage 영속화) */
    setTheme(state, action) {
      state.theme = action.payload;
      try { localStorage.setItem('buj-theme', action.payload); } catch (e) { /* noop */ }
    },
    /** 로그인 모달 표시 */
    showLoginModal(state) {
      state.loginModalVisible = true;
    },
    /** 로그인 모달 숨김 */
    hideLoginModal(state) {
      state.loginModalVisible = false;
    },
    /** 에러 상태 초기화 */
    clearError(state) {
      state.error = null;
    },
    /** 개발용 역할 토글 (guest ↔ admin) */
    setDevRole(state, action) {
      state.devRole = action.payload;
      try { localStorage.setItem('buj-dev-role', action.payload); } catch (e) { /* noop */ }
    },
  },
  extraReducers: (builder) => {
    // ── fetchUserInfo ──
    // 백엔드는 로그인 여부와 무관하게 항상 200을 반환한다.
    //   - 로그인됨: 실제 사용자 DTO (isGuest 없음 또는 false)
    //   - 비로그인: 권한 템플릿 ID=0 기반의 guest DTO (isGuest: true)
    // user는 양쪽 모두에 세팅되지만, isLoggedIn은 "실제 로그인"일 때만 true.
    // 이 덕분에 useAuth().hasPermission()은 guest 템플릿 기준으로 동작하고,
    // HomeNavBar/AppHeader 메뉴 필터링도 guest 기본 권한으로 처리된다.
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.user = action.payload;
        state.isLoggedIn = !action.payload?.isGuest;
      })
      .addCase(fetchUserInfo.rejected, (state) => {
        state.loading = false;
        state.initialized = true;
        // 네트워크 오류 등으로 get_userinfo 자체가 실패한 경우 — user를 비우고 비로그인 처리
        state.user = null;
        state.isLoggedIn = false;
      });

    // ── login ──
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isLoggedIn = true;
        state.loginModalVisible = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        // payload는 { code, message, meta } 객체일 수도 있고 문자열일 수도 있음
        const p = action.payload;
        state.error = typeof p === 'string' ? p : (p?.message || '로그인 실패');
        state.errorCode = typeof p === 'object' ? p?.code : null;
        state.errorMeta = typeof p === 'object' ? p?.meta : null;
      });

    // ── logout ──
    // 로그아웃 후 guest DTO로 user를 교체 (templateId=0 권한 적용)
    builder
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload || null;
        state.isLoggedIn = false; // guest 여부와 무관하게 로그아웃은 항상 false
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// 유틸 함수 내보내기 — 코드 기반 비밀번호 변경 페이지에서 재사용
// (레거시와 동일한 PBKDF2-SHA512 / iter=789,789 / salt=userId / keyLen=64 / Base64)
export { hashPassword };

// 액션 내보내기
export const { setLanguage, setTheme, showLoginModal, hideLoginModal, clearError, setDevRole } = authSlice.actions;

// 셀렉터 내보내기
export const selectUser = (state) => state.auth.user;
export const selectIsLoggedIn = (state) => state.auth.isLoggedIn;
export const selectAuthInitialized = (state) => state.auth.initialized;
export const selectLanguage = (state) => state.auth.language;
export const selectLoginModalVisible = (state) => state.auth.loginModalVisible;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectTheme = (state) => state.auth.theme;
export const selectAuthError = (state) => state.auth.error;
export const selectDevRole = (state) => state.auth.devRole;

export default authSlice.reducer;
